import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger";
import PuzzleBoard from "../components/games/story-puzzle/PuzzleBoard";
import PuzzleDifficultySelector from "../components/games/story-puzzle/PuzzleDifficultySelector";
import PuzzleVictoryModal from "../components/games/story-puzzle/PuzzleVictoryModal";
import { usePuzzleSoundManager } from "../components/games/story-puzzle/SoundManager";
import BackToGamesButton from "../components/games/BackToGamesButton";
import {
  resolvePuzzleImages,
  composePuzzleImage,
  buildInitialState,
  applyDrop,
  isSolved,
  placedCount,
  computeScore,
  ratingForSolve,
  evaluateLevel,
  clampLevel,
  loadDifficulty,
  saveDifficulty,
  formatTime,
} from "../components/games/story-puzzle/gameUtils";

const F = { fontFamily: "'Fredoka', sans-serif" };
const PUZZLE_GREEN = "#189970";

const StatChip = ({ icon, label, value }) => (
  <div
    className="inline-flex h-11 items-center gap-2 rounded-[15px] bg-white px-2 pr-3 sm:h-12"
    style={{
      border: "1.5px solid rgba(255,255,255,0.96)",
      boxShadow: "0 3px 0 rgba(17,122,89,0.09), 0 6px 14px rgba(11,26,51,0.1)",
      ...F,
    }}
  >
    <span
      className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] text-sm sm:h-9 sm:w-9"
      style={{ background: "rgba(24,153,112,0.14)", color: PUZZLE_GREEN }}
      aria-hidden
    >
      {icon}
    </span>
    <span className="flex flex-col items-start leading-none">
      <span className="mb-0.5 text-[8px] font-bold uppercase tracking-[0.09em] sm:text-[9px]" style={{ color: "#66788F" }}>
        {label}
      </span>
      <span className="text-lg font-bold tabular-nums sm:text-xl" style={{ color: "#222B54" }}>
        {value}
      </span>
    </span>
  </div>
);

const CONFETTI_COLORS = ["#189970", "#38BD87", "#FFC729", "#FF8A5B", "#66A8FF", "#FFFFFF"];

const Confetti = ({ trigger }) => {
  if (!trigger) return null;
  const bits = Array.from({ length: 70 }, (_, i) => ({
    id: `${trigger}-${i}`,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 0.4}s`,
    duration: `${1.6 + Math.random() * 1.2}s`,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 7 + Math.random() * 7,
  }));
  return (
    <div className="pointer-events-none fixed inset-0 z-[900] overflow-hidden" aria-hidden>
      {bits.map((b) => (
        <span
          key={b.id}
          style={{
            position: "absolute",
            top: "-5%",
            left: b.left,
            width: b.size,
            height: b.size * 0.6,
            background: b.color,
            borderRadius: 2,
            animation: `sp-confetti ${b.duration} ease-in ${b.delay} forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes sp-confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          80% { opacity: 0.9; }
          100% { transform: translateY(105vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const StoryPuzzlePage = () => {
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const navigate = useNavigate();
  const { play, prewarm } = usePuzzleSoundManager();

  const [status, setStatus] = useState("loading"); // loading | playing
  const [storyId, setStoryId] = useState(null);

  const [level, setLevel] = useState(3); // active grid (N×N)
  const [boardImage, setBoardImage] = useState(null);
  const [composited, setComposited] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const [slots, setSlots] = useState([]);
  const [bank, setBank] = useState([]);
  const [moves, setMoves] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const [finalScore, setFinalScore] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const [lastLevelChange, setLastLevelChange] = useState(0);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [goalOpen, setGoalOpen] = useState(false);
  // First-time nudge: the pieces tray usually sits below the fold, so point
  // at it until the child scrolls or touches a piece.
  const [showScrollHint, setShowScrollHint] = useState(false);

  const loadedLevelRef = useRef(3);
  const ratingsRef = useRef([]);
  const startTimeRef = useRef(null);
  const hasStartedRef = useRef(false);
  const hasSavedRef = useRef(false);
  const prepareGenRef = useRef(0);
  const poolRef = useRef([]);

  const grid = level;
  const total = grid * grid;
  const placed = placedCount(slots);

  // Freeze the grid, reset the round, pick a picture, and composite/slice it.
  const newGame = useCallback((nextLevel) => {
    const g = clampLevel(nextLevel);
    const t = g * g;
    hasStartedRef.current = false;
    hasSavedRef.current = false;
    startTimeRef.current = null;

    setLevel(g);
    const init = buildInitialState(t);
    setSlots(init.slots);
    setBank(init.bank);
    setMoves(0);
    setElapsed(0);
    setTimerRunning(false);
    setIsComplete(false);
    setFinalScore(0);
    setStarsAwarded(0);
    setIsNewBest(false);
    setLastLevelChange(0);

    const pool = poolRef.current;
    const chosen = pool.length ? pool[Math.floor(Math.random() * pool.length)] : null;
    setBoardImage(null);
    setComposited(false);
    setPreparing(true);
    prepareGenRef.current += 1;
    const gen = prepareGenRef.current;
    composePuzzleImage(chosen, 1200).then((res) => {
      if (prepareGenRef.current !== gen) return;
      setBoardImage(res.boardImage);
      setComposited(res.composited);
      setPreparing(false);
    });
  }, []);

  const loadGame = useCallback(async () => {
    if (!apiClient) return;
    setStatus("loading");
    const [gamesResult] = await Promise.allSettled([apiClient.getGames()]);
    const gamesResponse = gamesResult.status === "fulfilled" ? gamesResult.value : null;
    const payload = gamesResponse?.data || gamesResponse || {};
    logger.log("🧩 Story Puzzle getGames() response:", gamesResponse);

    const pool = resolvePuzzleImages(payload);
    poolRef.current = pool;
    setStoryId(payload?.story?.id || null);

    const stored = loadDifficulty();
    loadedLevelRef.current = stored.level;
    ratingsRef.current = stored.ratings;

    setStatus("playing");
    newGame(stored.level);
  }, [apiClient, newGame]);

  useEffect(() => {
    loadGame();
  }, [loadGame]);

  // Count-up timer (starts on first move; used only for the score, never a loss).
  useEffect(() => {
    if (!timerRunning || isComplete) return undefined;
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [timerRunning, isComplete]);

  const persistResult = useCallback(
    async (score) => {
      if (hasSavedRef.current || !apiClient) return;
      hasSavedRef.current = true;
      try {
        const response = await apiClient.postGameProgress("STORY_PUZZLE", score, true, storyId);
        if (response?.status || response?.success) {
          setStarsAwarded(Number(response?.stars_awarded || 0));
          const best = Number(response?.best_score ?? response?.data?.best_score ?? 0);
          setIsNewBest(response?.is_new_best === true || (best > 0 && best <= score));
          window.dispatchEvent(new CustomEvent("games:progress-updated"));
          refreshUserData?.();
        }
      } catch (error) {
        logger.warn("Story Puzzle save failed", error);
      }
    },
    [apiClient, storyId, refreshUserData]
  );

  const finish = useCallback(
    (finalMoves) => {
      const seconds = startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 1000)
        : 0;
      const score = computeScore(total, finalMoves, seconds);
      const rating = ratingForSolve(total, finalMoves, seconds);
      const next = evaluateLevel(level, ratingsRef.current, rating);
      loadedLevelRef.current = next.level;
      ratingsRef.current = next.ratings;
      saveDifficulty(next.level, next.ratings);

      setLastLevelChange(next.change);
      setFinalScore(score);
      setElapsed(seconds);
      setIsComplete(true);
      setTimerRunning(false);
      setConfettiTrigger((c) => c + 1);
      play("victory");
      persistResult(score);
    },
    [total, level, play, persistResult]
  );

  const handlePickup = useCallback(() => {
    prewarm();
    play("pickup");
    setShowScrollHint(false);
  }, [prewarm, play]);

  // Show the tray hint once the board has rendered taller than the viewport;
  // dismiss it on the first scroll (or first piece pickup, above).
  useEffect(() => {
    if (status !== "playing" || preparing) return undefined;
    const check = window.setTimeout(() => {
      const overflows =
        document.documentElement.scrollHeight > window.innerHeight + 60;
      if (overflows) setShowScrollHint(true);
    }, 600);
    const onScroll = () => {
      if (window.scrollY > 40) setShowScrollHint(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(check);
      window.removeEventListener("scroll", onScroll);
    };
  }, [status, preparing]);

  const handleDrop = useCallback(
    (piece, from, target) => {
      if (isComplete) return;
      if (!hasStartedRef.current) {
        hasStartedRef.current = true;
        startTimeRef.current = Date.now();
        setTimerRunning(true);
      }
      const nextMoves = moves + 1;
      setMoves(nextMoves);
      const nextState = applyDrop({ slots, bank }, piece, from, target);
      setSlots(nextState.slots);
      setBank(nextState.bank);
      play(target != null && target === piece ? "place" : "drop");
      if (isSolved(nextState.slots)) finish(nextMoves);
    },
    [isComplete, moves, slots, bank, play, finish]
  );

  const handleSelectLevel = useCallback(
    (nextLevel) => {
      if (isComplete || nextLevel === level) return;
      play("click");
      const clamped = clampLevel(nextLevel);
      ratingsRef.current = []; // a manual pick re-seeds the adaptive baseline
      loadedLevelRef.current = clamped;
      saveDifficulty(clamped, []);
      newGame(clamped);
    },
    [isComplete, level, play, newGame]
  );

  const handlePlayAgain = useCallback(() => {
    play("click");
    newGame(loadedLevelRef.current);
  }, [play, newGame]);

  const handleBackToGames = useCallback(() => {
    play("click");
    navigate("/games");
  }, [navigate, play]);

  return (
    <div
      className="relative min-h-screen overflow-hidden px-4 pb-6 pt-24 sm:px-6"
      style={{ ...F, background: "linear-gradient(180deg, #EDF8FF 0%, #D7EAFF 100%)" }}
    >
      {/* Soft watercolor scenery behind everything; the wash keeps contrast low
          so the board and chips stay readable on top. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "url(/assets/games/soft-meadow-bg.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center bottom",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(244,250,255,0.62) 0%, rgba(244,250,255,0.30) 45%, rgba(244,250,255,0.55) 100%)",
        }}
        aria-hidden
      />

      <div className="relative mx-auto w-full max-w-[1100px]">
        {status === "loading" && (
          <div className="space-y-4">
            <div className="h-24 animate-pulse rounded-[24px] bg-white/60 sm:h-28" />
            <div className="mx-auto h-[620px] w-full animate-pulse rounded-[28px] bg-white/45" />
          </div>
        )}

        {status === "playing" && (
          <div>
            <header
              className="mb-3 grid grid-cols-2 items-center gap-2 rounded-[24px] p-2.5 sm:mb-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-4 sm:p-3"
              style={{
                background: "rgba(237,251,246,0.84)",
                border: "1.5px solid rgba(255,255,255,0.9)",
                boxShadow: "0 5px 18px rgba(24,112,85,0.12)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
              }}
            >
              <div className="col-start-1 row-start-2 justify-self-start sm:row-start-1">
                <BackToGamesButton onClick={handleBackToGames} accent={PUZZLE_GREEN} />
              </div>

              <div className="col-span-2 row-start-1 min-w-0 text-center sm:col-span-1 sm:col-start-2">
                <h1
                  className="text-[28px] leading-none sm:text-[34px]"
                  style={{ color: PUZZLE_GREEN, fontWeight: 700, letterSpacing: "-0.025em" }}
                >
                  Story Puzzle
                </h1>
                <p className="mt-1.5 text-[13px] sm:text-sm" style={{ color: "#5D7695", fontWeight: 600 }}>
                  Drag each piece into place to complete the picture!
                </p>
              </div>

              <div className="col-start-2 row-start-2 flex items-center justify-self-end gap-2 sm:col-start-3 sm:row-start-1">
                <StatChip
                  icon={
                    <img
                      src="/assets/games/map-icons/story-puzzle.png"
                      alt=""
                      className="h-6 w-6 rounded-[8px] object-cover sm:h-7 sm:w-7"
                      draggable={false}
                    />
                  }
                  label="Pieces"
                  value={`${placed}/${total}`}
                />
                <StatChip
                  icon={
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
                      <circle cx="10" cy="11" r="7" stroke={PUZZLE_GREEN} strokeWidth="2" />
                      <path d="M10 7.5V11l2.5 1.8M8 2h4" stroke={PUZZLE_GREEN} strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  }
                  label="Time"
                  value={formatTime(elapsed)}
                />
                <button
                  type="button"
                  onClick={() => boardImage && setGoalOpen(true)}
                  aria-label="See the full goal picture"
                  className="flex h-11 items-center gap-2 rounded-[15px] bg-white p-1.5 transition-transform hover:scale-105 active:scale-100 sm:h-12 sm:pr-3"
                  style={{
                    border: "1.5px solid rgba(255,255,255,0.96)",
                    boxShadow: "0 3px 0 rgba(17,122,89,0.08), 0 6px 14px rgba(11,26,51,0.09)",
                    cursor: boardImage ? "zoom-in" : "default",
                    ...F,
                  }}
                >
                  {boardImage ? (
                    <img
                      src={boardImage}
                      alt="Completed puzzle goal"
                      className="h-8 w-8 rounded-[10px] object-cover sm:h-9 sm:w-9"
                      style={{ border: "1.5px solid rgba(24,153,112,0.34)" }}
                    />
                  ) : (
                    <span className="grid h-8 w-8 place-items-center rounded-[10px] sm:h-9 sm:w-9" style={{ background: "rgba(24,153,112,0.14)" }} aria-hidden>
                      <img
                        src="/assets/games/map-icons/story-puzzle.png"
                        alt=""
                        className="h-6 w-6 rounded-[8px] object-cover"
                        draggable={false}
                      />
                    </span>
                  )}
                  <span className="text-xs font-bold uppercase tracking-[0.08em]" style={{ color: "#5D7695" }}>
                    Goal
                  </span>
                </button>
              </div>
            </header>

            <main className="relative w-full">
              <div className="mb-3">
                <PuzzleDifficultySelector level={level} onSelect={handleSelectLevel} disabled={isComplete} />
              </div>

              <div
                className="mx-auto mt-2 max-w-[800px] rounded-[32px] p-3 sm:p-5"
                style={{
                  background:
                    "linear-gradient(165deg, #FFFDF6 0%, #FDF6E6 55%, #F9EFDD 100%)",
                  border: "2px solid rgba(255,255,255,0.95)",
                  boxShadow:
                    "0 2px 0 rgba(255,255,255,0.9) inset, 0 -6px 14px rgba(188,148,88,0.10) inset, 0 18px 40px rgba(24,80,112,0.16), 0 4px 12px rgba(24,80,112,0.10)",
                }}
              >
                <PuzzleBoard
                  slots={slots}
                  bank={bank}
                  grid={grid}
                  boardImage={boardImage}
                  composited={composited}
                  disabled={isComplete}
                  preparing={preparing}
                  onDrop={handleDrop}
                  onPickup={handlePickup}
                />
              </div>
            </main>
          </div>
        )}
      </div>

      {showScrollHint && !isComplete && (
        <button
          type="button"
          onClick={() => {
            setShowScrollHint(false);
            window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
          }}
          className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white px-4 py-2.5"
          style={{
            ...F,
            fontWeight: 700,
            color: PUZZLE_GREEN,
            border: "2px solid rgba(255,255,255,0.95)",
            boxShadow: "0 8px 22px rgba(11,26,51,0.22)",
            animation: "spHintBounce 1.6s ease-in-out infinite",
          }}
        >
          Your puzzle pieces are down here
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M8 2v10M3.5 8 8 12.5 12.5 8" stroke={PUZZLE_GREEN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <style>{`
            @keyframes spHintBounce {
              0%, 100% { transform: translate(-50%, 0); }
              50% { transform: translate(-50%, -7px); }
            }
            @media (prefers-reduced-motion: reduce) {
              button[style*="spHintBounce"] { animation: none; }
            }
          `}</style>
        </button>
      )}

      {goalOpen && (
        <div
          className="fixed inset-0 z-50 grid place-items-center p-6"
          style={{ background: "rgba(10,22,44,0.62)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
          onClick={() => setGoalOpen(false)}
          role="dialog"
          aria-label="Goal picture"
        >
          <div className="relative max-h-[82vh] max-w-[min(92vw,700px)]">
            <img
              src={boardImage}
              alt="The completed puzzle you are building"
              className="max-h-[82vh] w-full rounded-[24px] object-contain"
              style={{ border: "4px solid rgba(255,255,255,0.95)", boxShadow: "0 24px 60px rgba(0,0,0,0.4)" }}
            />
            <span
              className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1 text-xs font-bold uppercase tracking-[0.1em]"
              style={{ ...F, color: PUZZLE_GREEN, boxShadow: "0 4px 10px rgba(0,0,0,0.2)" }}
            >
              Goal
            </span>
          </div>
        </div>
      )}

      <Confetti trigger={confettiTrigger} />

      <PuzzleVictoryModal
        open={isComplete}
        boardImage={boardImage}
        score={finalScore}
        starsAwarded={starsAwarded}
        isNewBest={isNewBest}
        seconds={elapsed}
        moves={moves}
        levelChange={lastLevelChange}
        onPlayAgain={handlePlayAgain}
        onDone={handleBackToGames}
      />
    </div>
  );
};

export default StoryPuzzlePage;
